import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import './VoiceMessagePlayer.css';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VoiceMessagePlayer = ({ src, timestamp, className = '', style = {} }) => {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Generar barras de ondas estáticas (como WhatsApp)
  const waveBars = useMemo(() => {
    const bars = [];
    const count = 30;
    for (let i = 0; i < count; i++) {
      // Generar alturas aleatorias pero determinísticas (para que no cambien en re-renders)
      const seed = (i * 9301 + 49297) % 233280;
      const height = 20 + (seed / 233280) * 80;
      bars.push(height);
    }
    return bars;
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoaded = () => {
      setDuration(audio.duration || 0);
      setIsReady(true);
      setHasError(false);
    };
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleError = () => {
      console.error('Error cargando audio:', src);
      setIsReady(true);
      setHasError(true);
    };

    audio.addEventListener('loadedmetadata', handleLoaded);
    audio.addEventListener('durationchange', handleLoaded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Si ya está cargado
    if (audio.readyState >= 2) {
      handleLoaded();
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoaded);
      audio.removeEventListener('durationchange', handleLoaded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [src]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // Pausar otros audios en la página
      document.querySelectorAll('audio').forEach((a) => {
        if (a !== audio) {
          a.pause();
          a.currentTime = 0;
        }
      });
      audio.play().catch((err) => {
        console.error('Error reproduciendo audio:', err);
      });
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleProgressClick = (e) => {
    if (!progressRef.current || !audioRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`voice-message-wrapper ${className}`} style={style}>
      <div className="voice-message-player">
        <audio ref={audioRef} src={src} preload="metadata" />

        <button
          className="vmp-play-btn"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
        >
          {isPlaying ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="3" y="2" width="4" height="12" rx="1" />
              <rect x="9" y="2" width="4" height="12" rx="1" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <polygon points="4,2 14,8 4,14" />
            </svg>
          )}
        </button>

        <div className="vmp-waveform" ref={progressRef} onClick={handleProgressClick}>
          {waveBars.map((height, i) => {
            const barPercent = (i / waveBars.length) * 100;
            const isActive = barPercent < progressPercent;
            return (
              <div
                key={i}
                className={`vmp-wave-bar ${isActive ? 'active' : ''}`}
                style={{ height: `${height}%` }}
              />
            );
          })}
        </div>

        <span className="vmp-time">
          {hasError ? 'Error' : isReady ? formatTime(currentTime || duration) : '...'}
        </span>
      </div>
      {timestamp && (
        <span className="vmp-timestamp">{timestamp}</span>
      )}
    </div>
  );
};

export default VoiceMessagePlayer;
